import React, { useState, useEffect } from "react";
import {
  Users,
  PlusCircle,
  LayoutDashboard,
  UserCog,
  Calendar,
  BarChart3,
} from "lucide-react";
import { AuthProvider, useAuth } from "./modules/auth/context/AuthContext";
import {
  PacientesProvider,
  usePacientes,
} from "./modules/pacientes/context/PacientesContext";
import { UsuariosProvider } from "./modules/usuarios/context/UsuariosContext";
import { EvolucaoProvider } from "./modules/evolucao/context/EvolucaoContext";
import { MarcadoresProvider } from "./modules/marcadores/context/MarcadoresContext";
import { SorologiasProvider } from "./modules/sorologias/context/SorologiasContext";
import { ExamesProvider } from "./modules/exames/context/ExamesContext";
import { ExamesImagemProvider } from "./modules/exames-imagem/context/ExamesImagemContext";
import Login from "./modules/auth/components/LoginNovo";
import ModalTrocarSenha from "./shared/components/ModalTrocarSenha";
import CadastroUsuario from "./modules/auth/components/CadastroUsuario";
import Button from "./shared/components/ui/Button";
import Tabs from "./shared/components/ui/Tabs";
import CadastroPaciente from "./modules/pacientes/components/CadastroPaciente";
import ListaPacientes from "./modules/pacientes/components/ListaPacientes";
import VisualizacaoPaciente from "./modules/pacientes/components/VisualizacaoPaciente";
import Header from "./shared/components/layout/Header";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ProntuarioPage from "./modules/pacientes/pages/ProntuarioPage";
import TransparenciaPage from "./modules/legal/components/TransparenciaPage";
import ValidacaoPrescricao from "./modules/validacoes/components/ValidacaoPrescricao";
import PaginaSncr from "./modules/sncr/PaginaSncr";
import DashboardStats from "./modules/dashboard/components/DashboardStats";
import { AgendaProvider } from "./modules/agenda/context/AgendaContext";
import AbaAgenda from "./modules/agenda/components/AbaAgenda";
import GestaoUsuarios from "./modules/usuarios/pages/GestaoUsuarios";
import { theme } from "./shared/styles/theme"; // ⬅️ ADICIONAR ESTA LINHA AQUI
import { IAAnaliseProvider } from "./modules/ia-analise/context/IAAnaliseContext";
import { PendenciasProvider } from "./modules/pendencias/context/PendenciasContext";
import AbaPesquisaClinica from "./modules/pesquisa/components/AbaPesquisaClinica";
import { ehMedico } from "./shared/utils/permissoesCategoria";

// ✅ 1️⃣ PRIMEIRO: Função Dashboard
function Dashboard() {
  const { usuarioLogado, logout, trocarSenha } = useAuth(); // ⭐ ADICIONAR trocarSenha
  const {
    pacientes,
    pacienteSelecionado,
    selecionarPaciente,
    desselecionarPaciente,
  } = usePacientes();

  const [abaAtiva, setAbaAtiva] = useState("lista");

  return (
    <>
      <div className="min-h-screen bg-gray-100">
        {/* ✅ HEADER + ABAS FIXAS */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Header
            usuario={usuarioLogado}
            onLogout={logout}
            onTrocarSenha={trocarSenha} // ⭐ ADICIONAR esta linha
          />

          {/* ✅ ABAS FIXAS DENTRO DO HEADER */}
          <div
            style={{
              borderBottom: "1px solid #e5e7eb",
              background: "white",
              paddingTop: "12px",
            }}
          >
            <div className="max-w-7xl mx-auto px-4">
              <div style={{ display: "flex", gap: "8px" }}>
                {[
                  { id: "lista", label: "Pacientes Cadastrados", icon: Users },
                  // Novo Paciente: apenas admin e usuario
                  ...(usuarioLogado?.tipo !== "convidado"
                    ? [
                        {
                          id: "cadastro",
                          label: "Novo Paciente",
                          icon: PlusCircle,
                        },
                      ]
                    : []),
                  {
                    id: "dashboard",
                    label: "Dashboard",
                    icon: LayoutDashboard,
                  },
                  {
                    id: "agenda",
                    label: "Agenda",
                    icon: Calendar,
                  },
                  // 🔬 B242 — Pesquisa Clínica: só médico/admin (ato de pesquisa)
                  ...(ehMedico(usuarioLogado)
                    ? [
                        {
                          id: "pesquisa",
                          label: "Pesquisa Clínica",
                          icon: BarChart3,
                        },
                      ]
                    : []),
                  // Gestão de Usuários: apenas admin
                  ...(usuarioLogado?.tipo === "administrador"
                    ? [
                        {
                          id: "usuarios",
                          label: "Gestão de Usuários",
                          icon: UserCog,
                        },
                      ]
                    : []),
                ].map((aba) => (
                  <button
                    key={aba.id}
                    onClick={() => setAbaAtiva(aba.id)}
                    // 🎨 AI_116 — estilo "sublinhado" flat (mesmo das sub-abas da
                    // Pesquisa Clínica): sem gradiente/formato-pasta; ativa = texto
                    // azul + linha inferior azul; inativa = cinza (hover escurece).
                    style={{
                      padding: "12px 18px",
                      background: "none",
                      color:
                        abaAtiva === aba.id ? theme.primary[700] : theme.gray[500],
                      border: "none",
                      borderBottom:
                        abaAtiva === aba.id
                          ? `2px solid ${theme.primary[600]}`
                          : "2px solid transparent",
                      marginBottom: "-1px",
                      fontSize: "15px",
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: theme.fonts.sans,
                      transition: "color 0.2s ease, border-color 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onMouseOver={(e) => {
                      if (abaAtiva !== aba.id)
                        e.currentTarget.style.color = theme.gray[700];
                    }}
                    onMouseOut={(e) => {
                      if (abaAtiva !== aba.id)
                        e.currentTarget.style.color = theme.gray[500];
                    }}
                  >
                    <aba.icon size={18} />
                    <span>{aba.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ✅ CONTEÚDO COM SCROLL */}
        <main
          className="max-w-7xl mx-auto px-4 py-6"
          style={{
            marginTop: "170px",
          }}
        >
          {abaAtiva === "lista" && (
            <div>
              <div className="mb-4 flex justify-between items-center">
                <h2
                  className="text-2xl font-bold text-gray-800"
                  style={{
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  }}
                >
                  <Users size={18} style={{ marginRight: "8px" }} /> Pacientes (
                  {pacientes.length})
                </h2>
              </div>
              <ListaPacientes />
            </div>
          )}

          {abaAtiva === "cadastro" && usuarioLogado?.tipo !== "convidado" && (
            <CadastroPaciente />
          )}

          {abaAtiva === "dashboard" && <DashboardStats />}
          {abaAtiva === "agenda" && <AbaAgenda />}

          {/* 🔬 B242 — gate de papel espelha o menu (médico/admin) */}
          {abaAtiva === "pesquisa" && ehMedico(usuarioLogado) && (
            <AbaPesquisaClinica />
          )}

          {abaAtiva === "usuarios" && <GestaoUsuarios />}
        </main>
      </div>

      {/* Modal FORA do layout - sobreposto */}
      {pacienteSelecionado && <VisualizacaoPaciente />}
    </>
  );
}

// ✅ 2️⃣ DEPOIS: Função AppContent (usa Dashboard)
function AppContent() {
  const { usuarioLogado, loading, trocarSenha } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
        }}
      >
        <div style={{ textAlign: "center", color: "white" }}>
          <div
            style={{
              width: "60px",
              height: "60px",
              border: "6px solid rgba(255,255,255,0.3)",
              borderTop: "6px solid white",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          />
          <p
            style={{
              fontSize: "18px",
              fontWeight: "600",
              fontFamily: theme.fonts.sans,
            }}
          >
            Carregando...
          </p>
        </div>
      </div>
    );
  }

  if (!usuarioLogado) return <Login />;

  // 🔒 B103 — troca de senha obrigatória no 1º acesso: bloqueia o sistema até
  // que o usuário (criado com senha temporária) defina uma senha própria.
  if (usuarioLogado.precisaTrocarSenha) {
    return (
      <ModalTrocarSenha
        isPrimeiroAcesso
        onTrocarSenha={trocarSenha}
        onClose={() => {}}
      />
    );
  }

  return <Dashboard />;
}

// ✅ 3️⃣ POR ÚLTIMO: App principal
export default function App() {
  return (
    <AuthProvider>
      <IAAnaliseProvider>
        <UsuariosProvider>
          <PacientesProvider>
            <EvolucaoProvider>
              <ExamesProvider>
                <MarcadoresProvider>
                  <SorologiasProvider>
                    <ExamesImagemProvider>
                      <PendenciasProvider>
                        <AgendaProvider>
                          <BrowserRouter>
                            <Routes>
                              <Route
                                path="/cadastro"
                                element={<CadastroUsuario />}
                              />
                              <Route path="/" element={<AppContent />} />
                              <Route
                                path="/transparencia"
                                element={<TransparenciaPage />}
                              />
                              <Route
                                path="/validar"
                                element={<ValidacaoPrescricao />}
                              />
                              <Route
                                path="/validar/:codigo"
                                element={<ValidacaoPrescricao />}
                              />
                              {/* B356 (AI_154) — numeração do SNCR (login gov.br; só em hepato.app.br). */}
                              <Route path="/sncr" element={<PaginaSncr />} />
                              <Route
                                path="/prontuario/:id"
                                element={<ProntuarioPage />}
                              />
                              {/* Catch-all: evita tela em branco em rota desconhecida. */}
                              <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                          </BrowserRouter>
                        </AgendaProvider>
                      </PendenciasProvider>
                    </ExamesImagemProvider>
                  </SorologiasProvider>
                </MarcadoresProvider>
              </ExamesProvider>
            </EvolucaoProvider>
          </PacientesProvider>
        </UsuariosProvider>
      </IAAnaliseProvider>
    </AuthProvider>
  );
}
